'use client';

import { useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { X, ArrowLeft } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

// Portfolio data
const portfolioItems = [
  {
    id: 1,
    image: '/portfolio/1.jpg',
    title: 'Modern Web Design',
    description: 'A sleek and responsive website design featuring clean typography, intuitive navigation, and optimized user experience. Built with modern web technologies and following best practices for accessibility and performance.',
    category: 'Web Design',
    year: '2024'
  },
  {
    id: 2,
    image: '/portfolio/2.jpg',
    title: 'Mobile App Interface',
    description: 'Comprehensive mobile application design with focus on user-centered design principles. Features include seamless onboarding, intuitive gestures, and adaptive layouts for various screen sizes.',
    category: 'Mobile Design',
    year: '2024'
  },
  {
    id: 3,
    image: '/portfolio/3.jpg',
    title: 'Brand Identity System',
    description: 'Complete brand identity package including logo design, color palette, typography guidelines, and brand applications across various touchpoints. Creating cohesive visual communication.',
    category: 'Branding',
    year: '2023'
  },
  {
    id: 4,
    image: '/portfolio/4.jpg',
    title: 'E-commerce Platform',
    description: 'Full-featured e-commerce website with advanced filtering, seamless checkout process, and admin dashboard. Optimized for conversion and user engagement with modern design patterns.',
    category: 'Web Development',
    year: '2024'
  },
  {
    id: 5,
    image: '/portfolio/5.jpg',
    title: 'Creative Illustration',
    description: 'Custom illustration work combining traditional art techniques with digital tools. Focused on storytelling through visual elements and creating memorable brand imagery.',
    category: 'Illustration',
    year: '2023'
  },
  {
    id: 6,
    image: '/portfolio/6.jpg',
    title: 'Dashboard Design',
    description: 'Data visualization dashboard with complex information architecture. Features real-time analytics, customizable widgets, and intuitive data exploration tools for business intelligence.',
    category: 'UI/UX Design',
    year: '2024'
  },
  {
    id: 7,
    image: '/portfolio/7.jpg',
    title: 'Print Design Collection',
    description: 'Comprehensive print design portfolio including brochures, posters, and marketing materials. Emphasis on typography, layout composition, and print production optimization.',
    category: 'Print Design',
    year: '2023'
  },
  {
    id: 8,
    image: '/portfolio/8.jpg',
    title: 'Interactive Prototype',
    description: 'High-fidelity interactive prototype demonstrating complex user flows and micro-interactions. Built to validate design concepts and improve user testing outcomes.',
    category: 'Prototyping',
    year: '2024'
  },
  {
    id: 9,
    image: '/portfolio/9.jpg',
    title: 'Motion Graphics',
    description: 'Dynamic motion graphics and animation work for digital platforms. Combining visual storytelling with smooth animations to enhance user engagement and brand communication.',
    category: 'Motion Design',
    year: '2024'
  }
];

export default function Portfolio21() {
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const detailRef = useRef(null);
  const horizontalScrollRef = useRef(null);
  const figmaWindowRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailView, setIsDetailView] = useState(false);

  // Variable to control scroll height (you can adjust this value)
  const scrollHeight = 1500; // Height in vh (viewport height)

  useGSAP(() => {
    const horizontalContainer = horizontalScrollRef.current;
    const figmaWindow = figmaWindowRef.current;

    if (!horizontalContainer || !figmaWindow) return;

    // Function to calculate scroll distance
    const calculateScrollDistance = () => {
      const containerWidth = horizontalContainer.parentElement.offsetWidth;
      const contentWidth = horizontalContainer.scrollWidth;
      return contentWidth - containerWidth;
    };

    // Create timeline for the complete animation sequence
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top center",
        end: () => `+=${scrollHeight}vh`,
        scrub: 1,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        refreshPriority: -1,
        onUpdate: self => {
          // Optional: Add any updates during scroll
        }
      }
    });

    // First phase: Scale from 1 to 2 with easing (first 30% of scroll)
    tl.to(figmaWindow, {
      scale: 2,
      duration: 0.3,
      ease: "power2.out",
      transformOrigin: "center center"
    })
    // Second phase: Horizontal scroll (remaining 70% of scroll)
    .to(horizontalContainer, {
      x: () => -calculateScrollDistance(),
      duration: 0.7,
      ease: "none"
    });

    // Cleanup on unmount
    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [scrollHeight]);

  const openDetail = (item) => {
    setSelectedItem(item);
    setIsDetailView(true);

    // Animate grid out and detail in
    gsap.to(gridRef.current, {
      opacity: 0,
      scale: 0.9,
      duration: 0.4,
      ease: 'power2.out',
      onComplete: () => {
        gsap.set(detailRef.current, { display: 'flex' });
        gsap.fromTo(detailRef.current,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.2)' }
        );
      }
    });
  };

  const closeDetail = () => {
    gsap.to(detailRef.current, {
      opacity: 0,
      scale: 0.9,
      duration: 0.4,
      ease: 'power2.in',
      onComplete: () => {
        gsap.set(detailRef.current, { display: 'none' });
        setIsDetailView(false);
        setSelectedItem(null);
        gsap.fromTo(gridRef.current,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.2)' }
        );
      }
    });
  };

  return (
    <section className="first relative bg-white min-h-screen">

      <h1 className='text-3xl font-semibold mb-12 text-center pt-12'>Portfolio</h1>

      <div ref={containerRef} className="origin-center">
        <div className="relative w-full flex justify-center items-center">
          <img 
            ref={figmaWindowRef}
            className="block w-full max-w-6xl will-change-transform" 
            src="/portfolio/figma-window.png" 
            alt="" 
          />

          <div className="absolute top-[4.6%] left-[15.8%] right-[17.4%] bottom-[6%] p-24 overflow-hidden">
            {/* Grid View */}
            <div ref={gridRef} className="w-full h-full">
              <div
                ref={horizontalScrollRef}
                className="flex gap-8 flex-nowrap will-change-transform w-max"
              >
                {portfolioItems.map((item) => {
                  return (
                    <div
                      key={item.id}
                      className="flex-shrink-0 w-[25vw] cursor-pointer hover:scale-105 transition-transform duration-300"
                      onClick={() => openDetail(item)}
                    >
                      <div className="w-full h-64 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg shadow-lg flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mx-auto mb-4"></div>
                          <h3 className="text-lg font-semibold text-gray-800">{item.title}</h3>
                        </div>
                      </div>
                      <div className="mt-4">
                        <h3 className="text-lg font-semibold text-gray-800">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.category} • {item.year}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Detail View */}
            <div
              ref={detailRef}
              className="absolute inset-0 bg-white hidden flex-col justify-center items-center p-8 z-10"
            >
              {selectedItem && (
                <div className="max-w-4xl mx-auto text-center">
                  <button
                    onClick={closeDetail}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X size={24} />
                  </button>

                  <div className="mb-8">
                    <div className="w-full max-w-2xl mx-auto h-80 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg shadow-xl flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mx-auto mb-4"></div>
                        <h3 className="text-2xl font-bold text-gray-800">{selectedItem.title}</h3>
                      </div>
                    </div>
                  </div>

                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{selectedItem.title}</h2>
                  <p className="text-lg text-gray-600 mb-4">{selectedItem.category} • {selectedItem.year}</p>
                  <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto">{selectedItem.description}</p>

                  <button
                    onClick={closeDetail}
                    className="mt-8 flex items-center gap-2 mx-auto px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <ArrowLeft size={20} />
                    Back to Portfolio
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}